import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {funcOrValOr, minMax} from "./CommonJS";
import {componentInit, getElmOfRef, OverlayUnderpanel, useEffectTransition, useEventListener} from "./CommonReact";

export function usePopPanel(props) {
    const ctrl = useRef({}).current;

    if (componentInit(ctrl, props && [...Object.values(props)]))
        ctrl.panelJsx = <UIPopPanel {...{ctrl, ...props}}/>;

    return {panelJsx: ctrl.panelJsx, panelCtrl: ctrl};
}

export const enumOutClickModes = {
    noPropagate:    Symbol("outside click while panel is open will close panel and click will not propagate (default)."),
    propagate:      Symbol("...close panel and propagate."),
    propagateNoAnchor:  Symbol("...close panel and propagate unless click is on rAnchor."),
};

export function UIPopPanel({show, onClose, rAnchor, align, margin, ctrl, children, className, absPos, transition, classNameOverlay,
                               onAfterTransition, onHideDone, outClickMode}) {
    const vals = useRef({}).current;
    const [alt, setAlt] = useState({});             // show, rAnchor, content, className
    const [rPanel, setRPanel] = useState(null);
    const isShown = () => (alt.show ?? show) && !vals.forceClose;
    vals.forceClose = vals.forceClose && (alt.show ?? show);
    if (alt.hasOwnProperty("children"))
        children = alt.children;
    rAnchor = alt.rAnchor ?? rAnchor;
    outClickMode = alt.outClickMode ?? outClickMode;
    const {classNameTrans, propsTrans, refTrans} = useEffectTransition({
        transition: alt.transition ?? transition, isShown: isShown(),
        onAfter: (e, stage) => (onAfterTransition?.(e, stage), stage < 4? layoutPanel() : onHideDone?.())});

    if (ctrl)
        Object.assign(ctrl, {set: setAlt, isShown});

    useOutsideClick({rPanel, rAnchor, isEnabled: !!isShown(), outClickMode,
        onOutside: (e, sEv) => {
        e.preventDefault();
        if (!outClickMode || outClickMode === enumOutClickModes.noPropagate)
            e.stopPropagation();
        if (onClose?.cbIgnoreOutsideClicks?.() ||
            (sEv === "wheel" && classNameOverlay)) return;
        if (onClose) return onClose();
        vals.forceClose = true;         // mechanism when user clicked outside, but no external way to close panel.
        setAlt(st => ({}));
    }});

    function layoutPanel() {
        if (absPos || !classNameTrans) return;

        const rEl = getElmOfRef(rAnchor)?.getBoundingClientRect() ?? rAnchor;
        const rPan = getElmOfRef(rPanel)?.getBoundingClientRect();
        if (!rPan?.width || typeof rEl?.x !== "number") return;

        align = alt.align ?? align;
        margin = alt.margin ?? margin ?? 0;

        let left = minMax(rEl.x + (align?.includes("right") ? (rEl?.width ?? 0) - rPan.width : 0),
            0, document.body.clientWidth - rPan.width);

        let top = minMax(rEl.y + (align?.includes("above")? -rPan.height - margin : (rEl?.height ?? 0) + margin),
            0, document.body.clientHeight - rPan.height - margin);
        if (top < rEl.y + (rEl?.height ?? 0) && top + rPan.height >= rEl.y)     // panel overlaps Anchor?
            top = minMax(rEl.y + (!align?.includes("above")? -rPan.height - margin : (rEl?.height ?? 0) + margin),
                0, document.body.clientHeight - rPan.height - margin);

        Object.assign(rPanel.style, {left: left +"px", top: top +"px"});
    }

    useLayoutEffect(layoutPanel, [rAnchor, rPanel, children, align, margin, alt]);

    return createPortal(
        <OverlayUnderpanel {...{classNameOverlay, classNameTrans, onClose}}>
            {() =>
                <div ref={e => (refTrans(e), setTimeout(() => setRPanel(e)))} {...propsTrans}
                     className={`${classNameTrans} ${
                         absPos && (typeof absPos === "string"? absPos : "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2")} ${
                         className ?? alt.className}`}
                     style={{position: absPos? "absolute" : "fixed", zIndex: 50 + (absPos? 0 : 50)}}>
                    {funcOrValOr(children, {closePanel: () => setAlt({show: false})})}
                </div>
            }
        </OverlayUnderpanel>,
        document.body);
}

let nestedPanels = [];                                                              // list of all panels currently open

function stopPropagation(e) {
    e.stopPropagation()
    e.preventDefault();
}

function useOutsideClick({rPanel, rAnchor, onOutside, isEnabled = true, outClickMode = enumOutClickModes.noPropagate}) {
    const vals = useRef({}).current;

    if (vals.rPanel !== rPanel || vals.isEnabled !== !!isEnabled) {
        if (!rPanel || !isEnabled)
            nestedPanels.removeFirst(vals.rPanel);
        else if (vals.rPanel)
            nestedPanels.removeFirst(vals.rPanel, rPanel);          // replace
        else
            nestedPanels.push(rPanel);

        vals.rPanel = isEnabled && rPanel;
        vals.isEnabled = !!isEnabled;
    }

    useEffect(() => () => nestedPanels.removeFirst(vals.rPanel), []);       // on destruction, ensure removed from nestedPanels.

    for (let sEv of ["mousedown", "wheel", "touchmove"])                    // eslint-disable-next-line react-hooks/rules-of-hooks
        useEventListener({
            eOn: window, sEv, captureOrOpts: true, isEnabled,
            cb: e => {
                const target = e.composedPath?.()?.[0] || e.target;
                if (nestedPanels.last === vals.rPanel &&                                // rPanel is the latest in nesting
                    target?.getRootNode().contains(target) &&                           // target still exists in DOM (not sure why?)
                    !getElmOfRef(vals.rPanel)?.contains(target)) {                      // target is not under rPanel

                    onOutside(e, sEv);

                    // if clicked outside, and not requested to allow propagation or clicked on the rAnchor, then suppress click.
                    if (sEv === "mousedown" &&
                        (outClickMode === enumOutClickModes.noPropagate ||
                            (outClickMode === enumOutClickModes.propagateNoAnchor && getElmOfRef(rAnchor)?.contains(target)))) {
                        window.addEventListener('click', stopPropagation, true);
                        window.addEventListener('contextmenu', stopPropagation, true);
                        window.addEventListener('mouseup', e => setTimeout(() => {          // allow time for click or contextmenu to shoot before cancelling their handlers
                            window.removeEventListener('click', stopPropagation, true);
                            window.removeEventListener('contextmenu', stopPropagation, true);
                        }), {capture: true, once: true});
                    }
                }
            }
        });

    useEventListener({
        eOn: window, sEv: "keydown", isEnabled,
        cb: e => {
            if (e.key === "Escape" && nestedPanels.last === vals.rPanel) {
                e.stopPropagation();
                e.preventDefault();
                onOutside(e, "keydown");
            }
        }
    });
}
